import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import type {
  SalesOrderFulfillmentStatus as FulfillmentStatus,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'

/** The order fields a helper reads: a full order and an index row both carry them. */
type OrderFields<K extends keyof SalesOrderListItem> = Pick<SalesOrderListItem, K>

/** The order fields the fulfillment workflow reads. */
export type FulfillmentWorkflowOrder = OrderFields<
  | 'fulfillment_status'
  | 'fulfilled_at'
  | 'fulfillment_type'
  | 'order_source_link'
  | 'can_move_fulfillment_back'
>

/** A status a fulfillment action moves an order to. */
export type FulfillmentActionTargetStatus =
  | typeof SalesOrderFulfillmentStatus.ToDo.Value
  | typeof SalesOrderFulfillmentStatus.InProgress.Value
  | typeof SalesOrderFulfillmentStatus.ReadyForPickup.Value
  | typeof SalesOrderFulfillmentStatus.ReadyForDelivery.Value
  | typeof SalesOrderFulfillmentStatus.Out.Value
  | typeof SalesOrderFulfillmentStatus.Fulfilled.Value

export interface FulfillmentAction {
  targetStatus: FulfillmentActionTargetStatus
  /** Set only for out for delivery: FSN changes awaiting review block dispatch. */
  disabled?: boolean
  label: string
  menuLabel: string
  statusLabel: string
  iconName: string
  successMessage: string
  failureMessage: string
}

export function isFulfilledOrder(
  order: OrderFields<'fulfillment_status' | 'fulfilled_at'>
): boolean {
  return (
    order?.fulfillment_status === SalesOrderFulfillmentStatus.Fulfilled.Value ||
    Boolean(order?.fulfilled_at)
  )
}

function isPickupOrder(order: OrderFields<'fulfillment_type'>): boolean {
  return order?.fulfillment_type === FulfillmentType.Pickup.Value
}

function isDeliveryOrder(order: OrderFields<'fulfillment_type'>): boolean {
  return order?.fulfillment_type === FulfillmentType.Delivery.Value
}

export function getFulfillmentAction(
  order: OrderFields<'fulfillment_type' | 'order_source_link'>,
  targetStatus: FulfillmentStatus
): FulfillmentAction {
  // Only a delivered or picked-up order marked so by mistake goes back, to To Do.
  if (targetStatus === SalesOrderFulfillmentStatus.ToDo.Value) {
    const label = isDeliveryOrder(order) ? 'Mark Not Delivered' : 'Mark Not Picked Up'

    return {
      targetStatus,
      label,
      menuLabel: label,
      statusLabel: 'To Do',
      iconName: SalesOrderFulfillmentStatus.ToDo.Icon,
      successMessage: isDeliveryOrder(order)
        ? 'Order marked not delivered'
        : 'Order marked not picked up',
      failureMessage: isDeliveryOrder(order)
        ? 'Failed to mark the order not delivered'
        : 'Failed to mark the order not picked up',
    }
  }

  if (targetStatus === SalesOrderFulfillmentStatus.InProgress.Value) {
    return {
      targetStatus,
      label: 'Start Preparing',
      menuLabel: 'Preparing',
      statusLabel: 'In Progress',
      iconName: SalesOrderFulfillmentStatus.InProgress.Icon,
      successMessage: 'Order marked in progress',
      failureMessage: 'Failed to mark order in progress',
    }
  }

  if (targetStatus === SalesOrderFulfillmentStatus.ReadyForPickup.Value) {
    return {
      targetStatus,
      label: 'Mark Ready for Pickup',
      menuLabel: 'Ready for Pickup',
      statusLabel: 'Ready for Pickup',
      iconName: SalesOrderFulfillmentStatus.ReadyForPickup.Icon,
      successMessage: 'Order marked ready for pickup',
      failureMessage: 'Failed to mark order ready for pickup',
    }
  }

  if (targetStatus === SalesOrderFulfillmentStatus.ReadyForDelivery.Value) {
    return {
      targetStatus,
      label: 'Mark Ready for Delivery',
      menuLabel: 'Ready for Delivery',
      statusLabel: 'Ready for Delivery',
      iconName: SalesOrderFulfillmentStatus.ReadyForDelivery.Icon,
      successMessage: 'Order marked ready for delivery',
      failureMessage: 'Failed to mark order ready for delivery',
    }
  }

  if (targetStatus === SalesOrderFulfillmentStatus.Out.Value) {
    return {
      targetStatus,
      disabled: Boolean(order?.order_source_link?.fsn_change?.holds_dispatch),
      label: order?.order_source_link?.fsn_change?.holds_dispatch
        ? 'Review FSN changes before dispatch'
        : 'Mark Out for Delivery',
      menuLabel: 'Out for Delivery',
      statusLabel: 'Out for Delivery',
      iconName: SalesOrderFulfillmentStatus.Out.Icon,
      successMessage: 'Order marked out for delivery',
      failureMessage: 'Failed to mark order out for delivery',
    }
  }

  return {
    targetStatus: SalesOrderFulfillmentStatus.Fulfilled.Value,
    label: isDeliveryOrder(order) ? 'Mark Delivered' : 'Mark Picked Up',
    menuLabel: isDeliveryOrder(order) ? 'Delivered' : 'Picked Up',
    statusLabel: isDeliveryOrder(order) ? 'Delivered' : 'Picked Up',
    iconName: SalesOrderFulfillmentStatus.Fulfilled.Icon,
    successMessage: 'Order marked as fulfilled',
    failureMessage: 'Failed to mark order as fulfilled',
  }
}

function getWorkflowStatusSequence(
  order: OrderFields<'fulfillment_type'>
): FulfillmentActionTargetStatus[] {
  const status = SalesOrderFulfillmentStatus

  if (isPickupOrder(order)) {
    return [status.InProgress.Value, status.ReadyForPickup.Value, status.Fulfilled.Value]
  }

  if (isDeliveryOrder(order)) {
    return [
      status.InProgress.Value,
      status.ReadyForDelivery.Value,
      status.Out.Value,
      status.Fulfilled.Value,
    ]
  }

  return [status.Fulfilled.Value]
}

function getCurrentWorkflowStatus(
  order: OrderFields<'fulfillment_status' | 'fulfillment_type'>
): FulfillmentStatus | null {
  if (order?.fulfillment_status !== SalesOrderFulfillmentStatus.Staged.Value) {
    return order?.fulfillment_status
  }

  return isPickupOrder(order)
    ? SalesOrderFulfillmentStatus.InProgress.Value
    : SalesOrderFulfillmentStatus.ReadyForDelivery.Value
}

/**
 * Whether staff can move this completed order back to To Do. The order update decides it (`moveBackRefusal`) and the
 * order says so (`can_move_fulfillment_back`); the button reads that, so it offers only what the update accepts.
 */
export function canMoveFulfilledOrderBack(order: FulfillmentWorkflowOrder): boolean {
  return isFulfilledOrder(order) && order.can_move_fulfillment_back
}

export function getFulfillmentWorkflowActions(
  order: FulfillmentWorkflowOrder
): FulfillmentAction[] {
  if (isFulfilledOrder(order)) {
    return canMoveFulfilledOrderBack(order)
      ? [getFulfillmentAction(order, SalesOrderFulfillmentStatus.ToDo.Value)]
      : []
  }

  const currentStatus = getCurrentWorkflowStatus(order)

  return getWorkflowStatusSequence(order)
    .filter((targetStatus) => targetStatus !== currentStatus)
    .map((targetStatus) => getFulfillmentAction(order, targetStatus))
}

export function getNextFulfillmentWorkflowAction(
  order: FulfillmentWorkflowOrder
): FulfillmentAction | null {
  if (isFulfilledOrder(order)) {
    return null
  }

  const sequence = getWorkflowStatusSequence(order)
  const currentStatus = getCurrentWorkflowStatus(order)

  if (currentStatus === SalesOrderFulfillmentStatus.ToDo.Value) {
    const initialTargetStatus = isPickupOrder(order)
      ? SalesOrderFulfillmentStatus.ReadyForPickup.Value
      : SalesOrderFulfillmentStatus.Fulfilled.Value

    return getFulfillmentAction(order, initialTargetStatus)
  }

  const currentIndex = sequence.findIndex((status) => status === currentStatus)
  const nextStatus = sequence[currentIndex + 1] ?? sequence[0]

  return nextStatus ? getFulfillmentAction(order, nextStatus) : null
}
